import React, { useState, useRef, useCallback } from 'react';
import { Paperclip, AlertCircle, X } from 'lucide-react';
import Tooltip from '../Tooltip/Tooltip';

interface EnhancedAttachProps {
  onFilesSelected: (files: File[]) => void;
  acceptedFileTypes?: string;
  title?: string; 
}

/**
 * Enhanced file upload wrapper that adds drag-and-drop functionality
 * while preserving all existing upload features
 */
const EnhancedAttach: React.FC<EnhancedAttachProps> = ({
  onFilesSelected,
  acceptedFileTypes=".pdf,.csv,.doc,.docx,.xls,.xlsx,.html,.txt,.md,.png,.jpg,.jpeg,.gif,.webp",
  title = "Upload files"         
}) => {
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const [dragError, setDragError] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dropAreaRef = useRef<HTMLDivElement>(null);
  
  // Add states for file name validation
  const [showInvalidFileNameNotification, setShowInvalidFileNameNotification] = useState<boolean>(false);
  const [suggestedFileName, setSuggestedFileName] = useState<string>('');

  // File name validation function
const validateFileName = (_fileName: string): boolean => {
  // Any filename is accepted. The backend stores the original name in S3 and
  // sanitizes only the label sent to Bedrock, so special characters, unicode and
  // emoji no longer need to be blocked on the client.
  return true;
};
  
  // Function to create a valid filename suggestion
const createValidFileNameSuggestion = (originalName: string): string => {
  // Split the name to get the extension
  const lastDotIndex = originalName.lastIndexOf('.');
  let nameWithoutExt = originalName;
  let extension = '';
  
  if (lastDotIndex !== -1) {
    nameWithoutExt = originalName.substring(0, lastDotIndex);
    extension = originalName.substring(lastDotIndex); // Includes the dot
  }
  
  // Replace consecutive spaces with a single space
  let validName = nameWithoutExt.replace(/\s+/g, ' ');
  
  // Replace any invalid characters with underscores (now including underscore as valid)
  validName = validName.replace(/[^a-zA-Z0-9\s\-\(\)\[\]_]/g, '_');
  
  // Return the valid name with extension
  return validName + extension;
};
  const handleDragEnter = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isDragging) setIsDragging(true);
  }, [isDragging]);

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Only set isDragging to false if we're leaving the drop area
    if (!dropAreaRef.current?.contains(e.relatedTarget as Node)) {
      setIsDragging(false);
    }
  }, []);

  const validateFiles = useCallback((files: File[]) => {
    if (files.length === 0) return null;
    
    // Check for invalid file names
    const invalidFiles = files.filter(file => !validateFileName(file.name));
    
    if (invalidFiles.length > 0) {
      // Always show the notification and set suggested name
      setShowInvalidFileNameNotification(true);
      
      // Generate a suggestion for the first invalid file
      const suggestion = createValidFileNameSuggestion(invalidFiles[0].name);
      setSuggestedFileName(suggestion);
      
      return `Invalid filename${invalidFiles.length > 1 ? 's' : ''}`;
    }
    
    return null;
  }, []);

  const handleDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const droppedFiles = Array.from(e.dataTransfer.files);
    const error = validateFiles(droppedFiles);
    
    if (error) {
      setDragError(error);
      setTimeout(() => setDragError(''), 3000);
      return;
    }
    
    // Filter valid files only
    const validFiles = droppedFiles.filter(file => validateFileName(file.name));
    if (validFiles.length > 0) {
      onFilesSelected(validFiles);
    }
  }, [onFilesSelected, validateFiles]);

  const handleButtonClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      const selectedFiles = Array.from(event.target.files);
      
      // Always reset the input value immediately so the same file can be selected again
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      
      const error = validateFiles(selectedFiles);
      
      if (error) {
        setDragError(error);
        setTimeout(() => setDragError(''), 3000);
        return;
      }
      
      // Filter valid files only
      const validFiles = selectedFiles.filter(file => validateFileName(file.name));
      if (validFiles.length > 0) {
        onFilesSelected(validFiles);
      }
    }
  }, [onFilesSelected, validateFiles]);

  // Add this function to close the notification
  const handleCloseNotification = () => {
    setShowInvalidFileNameNotification(false);
    
    // Reset the suggested filename after a short delay
    // This ensures we can show the notification again for the same file
    setTimeout(() => {
      setSuggestedFileName('');
    }, 300);
  };

  // Global event handlers
  React.useEffect(() => {
    const preventDefaults = (e: Event) => {
      e.preventDefault();
      e.stopPropagation();
    };

    document.addEventListener('dragenter', preventDefaults);
    document.addEventListener('dragover', preventDefaults);
    document.addEventListener('dragleave', preventDefaults);
    document.addEventListener('drop', preventDefaults);

    // Add handler for clicking outside to close the notification
    const handleClickOutside = (e: MouseEvent) => {
      if (showInvalidFileNameNotification && 
          !(e.target as Element).closest('.file-name-notification') && 
          !(e.target as Element).closest('.file-upload-button')) {
        handleCloseNotification();
      }
    };
    
    document.addEventListener('click', handleClickOutside);

    return () => {
      document.removeEventListener('dragenter', preventDefaults);
      document.removeEventListener('dragover', preventDefaults);
      document.removeEventListener('dragleave', preventDefaults);
      document.removeEventListener('drop', preventDefaults);
      document.removeEventListener('click', handleClickOutside);
    };
  }, [showInvalidFileNameNotification]);

  return (
    <div className="relative">
    {/* Invalid filename notification */}
{showInvalidFileNameNotification && (
  <div className="fixed bottom-[70px] left-0 right-0 mx-auto w-[calc(100%-16px)] max-w-2xl z-50 px-2 sm:px-4 file-name-notification" style={{animation: 'fadeIn 0.3s ease-out forwards'}}>
    <div className="bg-amber-50 border border-amber-200 rounded-lg shadow-md p-2 sm:p-3 flex flex-col">
      <div className="flex items-start justify-between">
        <div className="flex items-start sm:items-center space-x-2">
          <AlertCircle className="text-amber-600 h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0 mt-1 sm:mt-0.5" />
          <p className="text-xs sm:text-sm text-amber-800">
            File names can only contain alphanumeric characters, spaces, hyphens, parentheses, 
            and square brackets. No consecutive spaces allowed.
          </p>
        </div>
        <button 
          onClick={handleCloseNotification} 
          className="text-amber-600 hover:text-amber-800 rounded-full p-1 transition-colors ml-1 sm:ml-2 flex-shrink-0"
          aria-label="Close notification"
        >
          <X className="h-4 w-4 sm:h-5 sm:w-5" />
        </button>
      </div>
      {suggestedFileName && (
        <div className="mt-1.5 sm:mt-2 text-xs sm:text-sm">
          <p className="font-medium text-amber-800">Suggested filename:</p>
          <div className="bg-white p-1 sm:p-1.5 rounded border border-amber-200 mt-1 text-gray-700 font-mono break-all text-xs sm:text-sm">
            {suggestedFileName}
          </div>
        </div>
      )}
    </div>
  </div>
)}
   
      {/* Global drag area that appears when files are dragged over the document */}
      {isDragging && (
        <div 
          ref={dropAreaRef}
          className="fixed inset-0 z-50 flex items-center justify-center bg-violet-100/90 border-2 border-dashed border-violet-500"
          onDragEnter={handleDragEnter}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <div className="text-center bg-white p-6 rounded-xl shadow-lg">
            <div className="mb-2">
              <Paperclip className="h-10 w-10 mx-auto text-violet-600" />
            </div>
            <p className="text-violet-800 font-medium text-lg">Drop files here</p>
            <p className="text-violet-600 text-sm">Drag and drop multiple files</p>
          </div>
        </div>
      )}

      {/* Error message */}
      {dragError && (
        <div className="absolute bottom-full mb-2 right-0 bg-red-100 text-red-700 px-3 py-1 rounded-lg flex items-center text-sm">
          <AlertCircle size={16} className="mr-1" />
          {dragError}
        </div>
      )}

      {/* The existing Attach component - we preserve all its functionality */}
      <div 
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
      >
        {/* Import and use the original Attach component here */}
        <Tooltip label={title} side="top">
          <button
            type="button"
            onClick={handleButtonClick}
            className="file-upload-button bg-surface-2 text-muted hover:bg-surface-3 hover:text-primary transition-colors duration-200 disabled:bg-surface-2 disabled:text-subtle disabled:cursor-not-allowed focus:outline-none ease-in-out select-none relative justify-center text-center rounded-full cursor-pointer active:scale-95 whitespace-nowrap inline-flex text-sm font-medium p-2"
          >
            <div
              className="flex items-center min-w-0 justify-center gap-1"
              style={{ maxWidth: "120px" }}
            >
              <Paperclip className="bg-background" size={17} />
              <div className="truncate leading-loose"></div>
            </div>
          </button>
        </Tooltip>
        <input
          ref={fileInputRef}
          id="file-input"
          type="file"
          multiple
          accept={acceptedFileTypes}
          className="hidden"
          onChange={handleFileChange}
        />
      </div>
    </div>
  );
};

export default EnhancedAttach;